import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, readConnection } from "../api/client";
import {
  proTeams,
  type TeamAction,
  type TeamCommand,
  type TeamOperation,
} from "../api/proTeams";
import { Badge, Button, Card, cx, StateBlock } from "../ui";
import { OperationDetails } from "../features/proTeams/OperationDetails";
import { RosterWorkflow } from "../features/proTeams/RosterWorkflow";
import {
  TeamActionDialog,
  actionTitles,
  type TeamForm,
} from "../features/proTeams/TeamActionDialog";
import {
  dateLabel,
  failureMessage,
  operationLabel,
  parseTeamId,
  rosterBlock,
  mergeOperations,
  blockingOperationId,
} from "../features/proTeams/state";

type SavedRequest = {
  action: TeamAction;
  teamId?: number;
  body: TeamCommand;
  key: string;
  uncertain?: boolean;
};
function stored<T>(key: string): T | null {
  try {
    return JSON.parse(sessionStorage.getItem(key) ?? "null") as T | null;
  } catch {
    return null;
  }
}
function save(key: string, value: unknown) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Page-local tracking still works when storage is disabled. */
  }
}
function pending(op?: TeamOperation) {
  return op?.status === "queued" || op?.status === "running";
}

export default function ProTeamsPage() {
  const connection = readConnection()?.baseUrl ?? "same-origin";
  const identity = useQuery({
    queryKey: ["pro-team-identity", connection],
    queryFn: () => proTeams.identity(),
    retry: false,
  });
  if (identity.isPending)
    return (
      <StateBlock
        title="Loading team access"
        detail="Checking your API permissions."
      />
    );
  if (identity.isError)
    return (
      <StateBlock
        tone="bad"
        title="Cannot load team access"
        detail={failureMessage(identity.error)}
        action={
          <Button onClick={() => void identity.refetch()}>Try again</Button>
        }
      />
    );
  const scope = `${connection}:${identity.data.name}`;
  return (
    <TeamWorkspace
      key={scope}
      scope={scope}
      canManage={identity.data.role === "organizer"}
    />
  );
}

function TeamWorkspace({
  scope,
  canManage,
}: {
  scope: string;
  canManage: boolean;
}) {
  const cache = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [tab, setTab] = useState<"roster" | "activity">("roster");
  const [form, setForm] = useState<TeamForm | null>(null);
  const [trackedId, setTrackedId] = useState<string | null>(() =>
    stored<string>(`team-operation:${scope}`),
  );
  const [saved, setSaved] = useState<SavedRequest | null>(() =>
    stored<SavedRequest>(`team-request:${scope}`),
  );
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [rosterBusy, setRosterBusy] = useState(false);
  const requestLock = useRef(false);
  const root = ["pro-teams", scope] as const;
  const recovery = useMutation({
    mutationFn: proTeams.recoverBot,
    retry: false,
    onSuccess: () => { void cache.invalidateQueries({ queryKey: root }); },
  });
  async function recoverBot(botId: string) {
    const result = await recovery.mutateAsync(botId);
    if (!result.quarantine_cleared) throw new Error("Bot quarantine was not cleared.");
    return result.message;
  }
  const list = useQuery({
    queryKey: [...root, "list", offset],
    queryFn: () => proTeams.list(20, offset),
    retry: false,
    refetchInterval: 15000,
  });
  const selectedId =
    parseTeamId(params.get("team") ?? "") ?? list.data?.teams[0]?.team_id;
  const detail = useQuery({
    queryKey: [...root, "detail", selectedId],
    queryFn: () => proTeams.get(selectedId!),
    enabled: selectedId !== undefined,
    retry: false,
    refetchInterval: 15000,
  });
  const team = detail.data;
  const capacity = useQuery({
    queryKey: [...root, "capacity", team?.bot_id],
    queryFn: () => proTeams.capacity(team!.bot_id),
    enabled: !!team,
    retry: false,
    refetchInterval: 15000,
  });
  const activity = useQuery({
    queryKey: [...root, "activity", selectedId, historyOffset],
    queryFn: () => proTeams.operations(selectedId!, 20, historyOffset),
    enabled: canManage && !!selectedId,
    retry: false,
    refetchInterval: 5000,
  });
  // Safety checks always use the newest history page, even while browsing older entries.
  const latest = useQuery({
    queryKey: [...root, "activity", selectedId, 0],
    queryFn: () => proTeams.operations(selectedId!, 20, 0),
    enabled: canManage && !!selectedId,
    retry: false,
    refetchInterval: 5000,
  });
  const operation = useQuery({
    queryKey: [...root, "operation", trackedId],
    queryFn: () => proTeams.operation(trackedId!),
    enabled: canManage && !!trackedId,
    retry: false,
    refetchInterval: (query) =>
      pending(query.state.data)
        ? 1500
        : query.state.data?.status === "unknown" ||
            query.state.data?.invitation_pending
          ? 5000
          : false,
  });
  const mutation = useMutation({
    mutationFn: (request: SavedRequest) =>
      proTeams.submit(
        request.action,
        request.teamId,
        request.body,
        request.key,
      ),
    retry: false,
    onSuccess: (op) => {
      save(`team-request:${scope}`, null);
      setSaved(null);
      setSubmitError(null);
      setForm(null);
      setTrackedId(op.operation_id);
      save(`team-operation:${scope}`, op.operation_id);
      cache.setQueryData([...root, "operation", op.operation_id], op);
      void cache.invalidateQueries({ queryKey: root });
    },
    onError: (error, request) => {
      setSubmitError(error);
      if (
        !request.uncertain &&
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        setSaved(null);
        save(`team-request:${scope}`, null);
        if (error.status === 409) {
          setForm(null);
          void cache.invalidateQueries({ queryKey: root });
        }
      } else {
        const uncertain = { ...request, uncertain: true };
        setSaved(uncertain);
        save(`team-request:${scope}`, uncertain);
        setForm(null);
      }
    },
    onSettled: () => {
      requestLock.current = false;
    },
  });
  const observed =
    mergeOperations(latest.data?.operations ?? [], operation.data).find(
      (op) => op.operation_id === trackedId,
    ) ?? operation.data;
  useEffect(() => {
    if (!observed || pending(observed)) return;
    void cache.invalidateQueries({ queryKey: ["pro-teams", scope, "list"] });
    void cache.invalidateQueries({ queryKey: ["pro-teams", scope, "detail"] });
    void cache.invalidateQueries({
      queryKey: ["pro-teams", scope, "capacity"],
    });
    void cache.invalidateQueries({
      queryKey: ["pro-teams", scope, "activity"],
    });
    if (
      (observed.action === "import" || observed.action === "create") &&
      observed.status === "succeeded" &&
      observed.team_id
    )
      setParams({ team: String(observed.team_id) });
  }, [
    observed?.operation_id,
    observed?.status,
    observed?.invitation_pending,
    cache,
    scope,
    setParams,
  ]);

  function submit(
    action: TeamAction,
    teamId: number | undefined,
    body: TeamCommand,
  ) {
    if (requestLock.current || saved || !canManage) return;
    const request = { action, teamId, body, key: crypto.randomUUID() };
    requestLock.current = true;
    setSaved(request);
    save(`team-request:${scope}`, request);
    setSubmitError(null);
    mutation.mutate(request);
  }
  function retry() {
    if (!saved || requestLock.current || !canManage) return;
    requestLock.current = true;
    setSubmitError(null);
    mutation.mutate(saved);
  }
  function open(next: TeamForm) {
    setSubmitError(null);
    setForm(next);
  }
  const trackedOnTeam = observed?.team_id === selectedId ? observed : undefined;
  const operations = mergeOperations(
    latest.data?.operations ?? [],
    trackedOnTeam,
  );
  const block = rosterBlock(operations);
  const busy =
    rosterBusy ||
    mutation.isPending ||
    !!saved ||
    pending(observed) ||
    (canManage && !!trackedId && (operation.isPending || operation.isError));
  const historyUnavailable = canManage && (latest.isPending || latest.isError);
  const editable =
    canManage &&
    team?.status === "active" &&
    !block &&
    !busy &&
    !historyUnavailable &&
    !detail.isError;
  const inProgress = operations.some(pending);
  const syncAllowed = canManage && !!team && !busy && !inProgress;
  const handover =
    team &&
    team.snapshot.admin_steam_id &&
    team.snapshot.admin_steam_id !== team.snapshot.bot_steam_id;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[.2em] text-gold-300">
            Team administration
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Dota teams</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-dim">
            Manage in-game rosters through dedicated bots.{" "}
            <Link
              className="text-gold-300 underline underline-offset-4"
              to="/teams"
            >
              Tournament lineups
            </Link>{" "}
            stay separate.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => open({ action: "create" })}
            >
              Create team
            </Button>
            <Button disabled={busy} onClick={() => open({ action: "import" })}>
              Connect existing team
            </Button>
          </div>
        )}
      </header>

      {!canManage && (
        <p className="rounded-lg border border-line bg-surface-1 px-4 py-3 text-sm text-ink-dim">
          Read-only access. An organizer can change rosters and team settings.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface-1/60 px-4 py-3 text-xs text-ink-dim">
        <span>Bots can manage multiple teams · Player acceptance required</span>
        <span>Incoming invitations are not available yet.</span>
      </div>

      {saved && !mutation.isPending && (
        <div
          role="alert"
          className="space-y-3 rounded-lg border border-warn/50 bg-warn/5 p-4"
        >
          <p className="font-medium text-warn">
            Request acceptance is unconfirmed
          </p>
          <p className="text-sm text-ink-dim">
            The request may have reached the API. Retry its original key to
            check safely; a new action is blocked until this is resolved.
          </p>
          <Button onClick={retry} disabled={!canManage}>
            Retry same request
          </Button>
        </div>
      )}
      {!!submitError && !form && (
        <div
          role="alert"
          className="rounded-lg border border-bad/40 p-4 text-sm text-bad"
        >
          <p>{failureMessage(submitError)}</p>
          {canManage && blockingOperationId(submitError) && (
            <Button className="mt-3" disabled={busy} onClick={() => {
              const id = blockingOperationId(submitError)!;
              setTrackedId(id);
              save(`team-operation:${scope}`, id);
            }}>
              Inspect blocking operation
            </Button>
          )}
        </div>
      )}
      {canManage && observed && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface-2/70 px-4 py-3"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {actionTitles[observed.action as TeamAction] ?? observed.action}{" "}
              <span className="ml-2 font-normal text-ink-dim">
                {observed.team_id
                  ? `Team ${observed.team_id}`
                  : observed.bot_id}
              </span>
            </p>
          </div>
          <Badge
            tone={
              observed.status === "failed"
                ? "bad"
                : observed.status === "unknown" || observed.invitation_pending
                  ? "warn"
                  : observed.status === "succeeded"
                    ? "ok"
                    : "info"
            }
          >
            {operationLabel(observed)}
          </Badge>
          <div className="w-full min-w-0">
            <OperationDetails operation={observed} onRecover={canManage ? recoverBot : undefined} recovering={recovery.isPending} />
            {observed.team_id &&
              ((observed.action === "create" &&
                observed.status === "unknown" &&
                observed.gc_result === 0) ||
                (observed.action === "import" &&
                  ["failed", "unknown"].includes(observed.status))) && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-ink-dim">
                    Verify the existing team with {observed.bot_id}. This does
                    not create another team.
                  </p>
                  <Button
                    disabled={busy}
                    onClick={() => {
                      if (team?.team_id === observed.team_id)
                        submit("sync", observed.team_id, {});
                      else
                        submit("import", undefined, {
                          bot_id: observed.bot_id,
                          team_id: observed.team_id,
                        });
                    }}
                  >
                    Recheck existing team
                  </Button>
                </div>
              )}
          </div>
        </div>
      )}
      {operation.isError && (
        <p role="alert" className="text-sm text-warn">
          Operation tracking is unavailable.{" "}
          <button
            className="underline"
            onClick={() => void operation.refetch()}
          >
            Retry status check
          </button>
        </p>
      )}

      {list.isPending ? (
        <StateBlock title="Loading managed teams" />
      ) : list.isError ? (
        <StateBlock
          tone="bad"
          title="Team management is unavailable"
          detail={failureMessage(list.error)}
          action={
            <Button onClick={() => void list.refetch()}>
              Retry connection
            </Button>
          }
        />
      ) : (
        <div className="grid items-start gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-3" aria-label="Managed teams">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dim">
                Managed teams
              </h2>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void list.refetch()}
              >
                Refresh
              </Button>
            </div>
            {list.data.teams.length === 0 ? (
              <div className="rounded-lg border border-dashed border-line p-5 text-sm text-ink-dim">
                No teams on this page.
              </div>
            ) : (
              <div className="space-y-2">
                {list.data.teams.map((item) => (
                  <button
                    key={item.team_id}
                    onClick={() => {
                      setParams({ team: String(item.team_id) });
                      setHistoryOffset(0);
                      setTab("roster");
                    }}
                    aria-pressed={item.team_id === selectedId}
                    className={cx(
                      "w-full rounded-lg border p-4 text-left transition-colors",
                      item.team_id === selectedId
                        ? "border-gold-400/60 bg-gold-400/5"
                        : "border-line bg-surface-1 hover:border-line-strong",
                    )}
                  >
                    <span className="block truncate text-sm font-semibold">
                      {item.snapshot.name || `Team ${item.team_id}`}
                    </span>
                    <span className="mt-1 block break-all text-xs text-ink-dim">
                      {item.snapshot.tag || "No tag"} · {item.team_id}
                    </span>
                    <span className="mt-3 block text-xs text-ink-faint">
                      {item.snapshot.members.length} members ·{" "}
                      {item.status.replaceAll("_", " ")}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-between gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 20))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={list.data.teams.length < 20}
                onClick={() => setOffset(offset + 20)}
              >
                Next
              </Button>
            </div>
          </aside>

          <div className="min-w-0 space-y-5">
            {!selectedId ? (
              <Card>
                <StateBlock
                  title="Add your first Dota team"
                  detail="Create a team through a dedicated bot, or connect an existing team the bot administers."
                  action={
                    canManage ? (
                      <Button
                        variant="primary"
                        disabled={busy}
                        onClick={() => open({ action: "import" })}
                      >
                        Connect a team
                      </Button>
                    ) : undefined
                  }
                />
              </Card>
            ) : detail.isPending ? (
              <Card>
                <StateBlock title="Loading roster" />
              </Card>
            ) : detail.isError ? (
              <Card>
                <StateBlock
                  tone="bad"
                  title="Cannot load this team"
                  detail={failureMessage(detail.error)}
                  action={
                    <Button onClick={() => void detail.refetch()}>
                      Retry team
                    </Button>
                  }
                />
              </Card>
            ) : (
              team && (
                <>
                  <Card
                    className="overflow-hidden"
                    title={
                      <span className="text-xl">
                        {team.snapshot.name || `Team ${team.team_id}`}
                      </span>
                    }
                    description={`Dota team ${team.team_id} · ${team.snapshot.tag || "No tag"}`}
                    actions={
                      <>
                        <Badge
                          tone={team.status === "active" ? "ok" : "neutral"}
                        >
                          {team.status.replaceAll("_", " ")}
                        </Badge>
                        {canManage && (
                          <Button
                            size="sm"
                            disabled={!syncAllowed}
                            onClick={() => submit("sync", team.team_id, {})}
                          >
                            Sync roster
                          </Button>
                        )}
                      </>
                    }
                  >
                    <div className="mb-5 flex flex-wrap items-center gap-3">
                      {team.snapshot.logo_url?.startsWith("https://") && (
                        <img
                          src={team.snapshot.logo_url}
                          alt={`${team.snapshot.name} logo`}
                          className="h-16 w-16 rounded-lg object-contain"
                          referrerPolicy="no-referrer"
                        />
                      )}
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            disabled={!editable}
                            onClick={() => open({ action: "logo", team })}
                          >
                            Upload team interface logo
                          </Button>
                          <Button
                            size="sm"
                            disabled={!editable}
                            onClick={() => open({ action: "banner", team })}
                          >
                            Upload banner logo
                          </Button>
                          <Button
                            size="sm"
                            disabled={!editable}
                            onClick={() => open({ action: "sponsor", team })}
                          >
                            Upload sponsor logo
                          </Button>
                          <Button
                            size="sm"
                            disabled={!editable}
                            onClick={() => open({ action: "base", team })}
                          >
                            Upload base logo
                          </Button>
                        </>
                      )}
                      <span className="text-xs text-ink-dim">
                        {team.snapshot.logo_id ? "Logo set" : "No logo"} ·{" "}
                        {team.snapshot.banner_id ? "Banner set" : "No banner"} |{" "}
                        {team.snapshot.sponsor_id
                          ? "Sponsor logo set"
                          : "No sponsor logo"}{" "}
                        ?{" "}
                        {team.snapshot.base_id
                          ? "Base logo set"
                          : "No base logo"}
                      </span>
                    </div>
                    <div className="mb-5 flex items-start justify-between gap-4">
                      <p className="whitespace-pre-wrap break-words text-sm text-ink-dim">
                        {team.description || "No team description yet."}
                      </p>
                      {canManage && (
                        <Button
                          size="sm"
                          disabled={!editable}
                          onClick={() => open({ action: "description", team })}
                        >
                          Edit description
                        </Button>
                      )}
                    </div>
                    <dl className="grid gap-4 text-sm sm:grid-cols-3">
                      <div>
                        <dt className="text-xs text-ink-dim">Managing bot</dt>
                        <dd className="mt-1 break-all font-medium">
                          {team.bot_id}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-dim">
                          Last confirmed snapshot
                        </dt>
                        <dd className="mt-1">{dateLabel(team.updated_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-dim">Bot capacity</dt>
                        <dd className="mt-1">
                          {capacity.data
                            ? `${capacity.data.managed_teams}${capacity.data.policy_max_managed_teams == null ? "" : ` / ${capacity.data.policy_max_managed_teams}`} managed teams`
                            : "Capacity unavailable"}
                        </dd>
                        <dd className="mt-1 text-xs text-ink-dim">
                          {capacity.data?.verified_remote_membership_limit ==
                          null
                            ? "Valve limit unverified"
                            : `Verified remote limit: ${capacity.data.verified_remote_membership_limit}`}
                        </dd>
                      </div>
                    </dl>
                    {capacity.isError && (
                      <p className="mt-3 text-xs text-warn">
                        Capacity could not be refreshed.{" "}
                        <button
                          className="underline"
                          onClick={() => void capacity.refetch()}
                        >
                          Retry
                        </button>
                      </p>
                    )}
                  </Card>
                  {block && (
                    <p
                      role="status"
                      className="rounded-lg border border-warn/40 bg-warn/5 p-4 text-sm text-warn"
                    >
                      {block}
                    </p>
                  )}
                  {canManage && latest.isError && (
                    <p role="alert" className="text-sm text-warn">
                      Operation history is unavailable. Roster changes are
                      paused until it can be checked.{" "}
                      <button
                        className="underline"
                        onClick={() => void latest.refetch()}
                      >
                        Retry history
                      </button>
                    </p>
                  )}
                  <div
                    className="flex gap-5 border-b border-line"
                    role="group"
                    aria-label="Team details"
                  >
                    <button
                      aria-pressed={tab === "roster"}
                      className={cx(
                        "border-b-2 pb-3 text-sm",
                        tab === "roster"
                          ? "border-gold-400 text-gold-300"
                          : "border-transparent text-ink-dim",
                      )}
                      onClick={() => setTab("roster")}
                    >
                      Roster{" "}
                      <span className="ml-1 text-xs">
                        {team.snapshot.members.length}
                      </span>
                    </button>
                    {canManage && (
                      <button
                        aria-pressed={tab === "activity"}
                        className={cx(
                          "border-b-2 pb-3 text-sm",
                          tab === "activity"
                            ? "border-gold-400 text-gold-300"
                            : "border-transparent text-ink-dim",
                        )}
                        onClick={() => setTab("activity")}
                      >
                        Activity
                      </button>
                    )}
                  </div>
                  {tab === "roster" ? (
                    <div
                      role="region"
                      aria-label="Roster"
                      className="space-y-5"
                    >
                      {canManage && <RosterWorkflow key={`${scope}:${team.team_id}`} team={team} scope={scope}
                        operations={operations} onInvite={target => open({ action: "invite", team, target })}
                        disabled={!editable} onBusy={setRosterBusy}
                        onChanged={() => { void cache.invalidateQueries({ queryKey: root }); }}
                        onInspect={id => { setTrackedId(id); save(`team-operation:${scope}`, id); }}
                      />}
                      <Card
                        title="Team members"
                        description="Confirmed in-game membership. Match lineups are managed separately."
                        actions={
                          canManage ? (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={!editable}
                              onClick={() => open({ action: "invite", team })}
                            >
                              Invite player
                            </Button>
                          ) : undefined
                        }
                      >
                        {team.snapshot.members.length === 0 ? (
                          <StateBlock
                            title="No confirmed members"
                            detail="Sync the roster to check the current team state."
                          />
                        ) : (
                          <ul className="divide-y divide-line">
                            {team.snapshot.members.map((member) => (
                              <li
                                key={member.steam_id}
                                className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0"
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <div
                                    aria-hidden="true"
                                    className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-2 text-xs text-gold-300"
                                  >
                                    {member.admin ? "AD" : "PL"}
                                  </div>
                                  <div>
                                    <a
                                      href={`https://steamcommunity.com/profiles/${member.steam_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="break-all font-mono text-xs text-ink hover:underline"
                                    >
                                      {member.steam_id}
                                    </a>
                                    <p className="mt-1 text-xs text-ink-dim">
                                      {member.steam_id ===
                                      team.snapshot.bot_steam_id
                                        ? "Managing bot"
                                        : member.admin
                                          ? "Administrator"
                                          : "Player"}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {member.admin && (
                                    <Badge tone="gold">Admin</Badge>
                                  )}
                                  {canManage &&
                                    member.steam_id !==
                                      team.snapshot.bot_steam_id &&
                                    !member.admin && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          disabled={!editable}
                                          aria-label={`Make ${member.steam_id} admin`}
                                          onClick={() =>
                                            open({
                                              action: "transfer",
                                              team,
                                              target: member.steam_id,
                                            })
                                          }
                                        >
                                          Make admin
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="danger"
                                          disabled={!editable}
                                          aria-label={`Remove ${member.steam_id}`}
                                          onClick={() =>
                                            open({
                                              action: "kick",
                                              team,
                                              target: member.steam_id,
                                            })
                                          }
                                        >
                                          Remove
                                        </Button>
                                      </>
                                    )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </Card>
                      {canManage && (
                        <Card
                          title="Team settings"
                          description="Changes affect this persistent Dota team."
                        >
                          <div className="flex flex-wrap gap-2">
                            <Button
                              disabled={!editable}
                              onClick={() => open({ action: "edit", team })}
                            >
                              Edit details
                            </Button>
                            <Button
                              variant="danger"
                              disabled={
                                busy ||
                                !!block ||
                                historyUnavailable ||
                                !["active", "read_only"].includes(team.status)
                              }
                              onClick={() => open({ action: "archive", team })}
                            >
                              Archive team
                            </Button>
                            <Button
                              variant="danger"
                              disabled={
                                busy ||
                                !!block ||
                                historyUnavailable ||
                                !handover ||
                                !["active", "read_only"].includes(team.status)
                              }
                              onClick={() => open({ action: "leave", team })}
                            >
                              Bot leaves team
                            </Button>
                          </div>
                          <p className="mt-3 text-xs text-ink-dim">
                            Before the bot leaves, transfer administration to
                            another member. Archiving retains remote membership
                            and capacity.
                          </p>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <Card
                      title="Operation history"
                      description="A confirmed invite send can still be awaiting the player’s response."
                    >
                      {activity.isPending ? (
                        <StateBlock title="Loading activity" />
                      ) : activity.isError ? (
                        <StateBlock
                          tone="bad"
                          title="Cannot load activity"
                          action={
                            <Button onClick={() => void activity.refetch()}>
                              Retry
                            </Button>
                          }
                        />
                      ) : activity.data.operations.length === 0 ? (
                        <StateBlock
                          title="No recorded operations"
                          detail="Invites, roster updates, and administrative changes will appear here."
                        />
                      ) : (
                        <ul className="divide-y divide-line">
                          {activity.data.operations.map((op) => (
                            <li
                              key={op.operation_id}
                              className="space-y-2 py-4 first:pt-0"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-medium">
                                  {actionTitles[op.action as TeamAction] ??
                                    op.action}
                                </p>
                                <Badge
                                  tone={
                                    op.status === "unknown" ||
                                    op.invitation_pending
                                      ? "warn"
                                      : op.status === "failed"
                                        ? "bad"
                                        : "neutral"
                                  }
                                >
                                  {operationLabel(op)}
                                </Badge>
                              </div>
                              <p className="break-all text-xs text-ink-dim">
                                {op.command.steam_id &&
                                  `${op.command.steam_id} · `}
                                {op.actor} · {dateLabel(op.updated_at)}
                              </p>
                              <OperationDetails operation={op} onRecover={canManage ? recoverBot : undefined} recovering={recovery.isPending} />
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-4 flex justify-between gap-2 border-t border-line pt-4">
                        <Button
                          size="sm"
                          disabled={historyOffset === 0}
                          onClick={() =>
                            setHistoryOffset(Math.max(0, historyOffset - 20))
                          }
                        >
                          Newer
                        </Button>
                        <Button
                          size="sm"
                          disabled={
                            (activity.data?.operations.length ?? 0) < 20
                          }
                          onClick={() => setHistoryOffset(historyOffset + 20)}
                        >
                          Older
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              )
            )}
          </div>
        </div>
      )}
      {form && (
        <TeamActionDialog
          key={`${form.action}:${form.team?.team_id}:${form.target}`}
          form={form}
          scope={scope}
          locked={mutation.isPending || !!saved}
          error={submitError}
          onClose={() => {
            if (!mutation.isPending && !saved) setForm(null);
          }}
          onSubmit={submit}
        />
      )}
    </div>
  );
}
